import { Anchor, Table } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { datum, datumZaApi, denar } from '../oblika/oblika';
import { DaNe, Obdobje } from '../seznam/Filtri';
import { GlavaZaslona, IskalnoPolje, Seznam, Stolpec, ZaslonSeznama } from '../seznam/Seznam';
import { daNeZaApi, useSeznam } from '../seznam/useSeznam';

/** Ključi razvrščanja (PredracuniKontroler.pregled). */
type Razvrsti = 'stevilka' | 'datum' | 'potek' | 'znesek' | 'racun' | 'kupec' | 'uporabnik';

/**
 * Pregled predračunov: iskanje, obdobje, »Izvožen v račun« in »Potečen« (Vsi/Da/Ne),
 * razvrščanje in strani na strežniku. Klik na vrstico odpre podrobnosti.
 */
export function PregledPredracunov() {
  const seznam = useSeznam<Razvrsti>();
  const navigate = useNavigate();
  const od = seznam.parameter('od');
  const doDatuma = seznam.parameter('do');
  const izvozen = daNeZaApi(seznam.parameter('izvozen'));
  const potecen = daNeZaApi(seznam.parameter('potecen'));
  const poizvedba = useQuery({
    queryKey: ['predracuni', seznam.iskanje, od, doDatuma, izvozen, potecen, seznam.zaApi],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/predracuni/pregled', {
          signal,
          params: {
            query: {
              iskanje: seznam.iskanje || undefined,
              od: datumZaApi(od),
              do: datumZaApi(doDatuma),
              izvozen,
              potecen,
              ...seznam.zaApi,
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const vrstice = poizvedba.data?.vsebina ?? [];

  return (
    <ZaslonSeznama>
      <GlavaZaslona naslov="Predračuni" opis="Pregled izdanih predračunov." />
      <Seznam
        seznam={seznam}
        filtri={
          <>
            <IskalnoPolje seznam={seznam} namig="Išči po kupcu, davčni številki, številki predračuna ali znesku" />
            <Obdobje seznam={seznam} />
            <DaNe seznam={seznam} ime="izvozen" oznaka="Izvožen v račun" />
            <DaNe seznam={seznam} ime="potecen" oznaka="Potečen" />
          </>
        }
        glava={
          <>
            <Stolpec seznam={seznam} kljuc="stevilka" naziv="Št. pred." />
            <Stolpec seznam={seznam} kljuc="datum" naziv="Ustvarjen" />
            <Stolpec seznam={seznam} kljuc="potek" naziv="Potek" />
            <Stolpec seznam={seznam} kljuc="znesek" naziv="Znesek" desno />
            <Stolpec seznam={seznam} kljuc="racun" naziv="V račun" />
            <Stolpec seznam={seznam} kljuc="kupec" naziv="Stranka" />
            <Stolpec seznam={seznam} kljuc="uporabnik" naziv="Uporabnik" />
          </>
        }
        vrstice={vrstice.map((p, i) => (
          <Table.Tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => void navigate(`/predracuni/${p.id}`)}>
            <Table.Td c="dimmed" className="brez-preloma">
              {seznam.zamik + i + 1}
            </Table.Td>
            <Table.Td>
              <Anchor
                component={Link}
                to={`/predracuni/${p.id}`}
                onClick={(e) => e.stopPropagation()}
                fw={600}
                className="brez-preloma"
              >
                {p.stPredracuna}
              </Anchor>
            </Table.Td>
            <Table.Td className="brez-preloma">{datum(p.datum)}</Table.Td>
            <Table.Td className="brez-preloma">{datum(p.veljavnoDo)}</Table.Td>
            <Table.Td ta="right" className="brez-preloma">
              {denar(p.znesek)}
            </Table.Td>
            <Table.Td className="brez-preloma">{p.stRacuna}</Table.Td>
            <Table.Td>{p.kupec}</Table.Td>
            <Table.Td>{p.uporabnikVozila}</Table.Td>
          </Table.Tr>
        ))}
        stVrstic={vrstice.length}
        stStolpcev={7}
        skupaj={poizvedba.data?.skupaj ?? 0}
        nalaganje={poizvedba.isPending}
        osvezevanje={poizvedba.isFetching}
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        prazno="Ni predračunov za izbrane pogoje."
        napakaNaslov="Predračunov ni bilo mogoče naložiti"
      />
    </ZaslonSeznama>
  );
}
